import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
// Versión "legacy" de pdf.js: incluye polyfills para navegadores móviles más antiguos
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";

/*
 * El worker se sirve tal cual desde public/ (lo copia scripts/copiar-pdf-worker.js
 * antes de start/build). Si pasa por el bundler, Babel le agrega imports con rutas
 * absolutas de la máquina de compilación y falla en producción.
 */
pdfjsLib.GlobalWorkerOptions.workerSrc =
  `${process.env.PUBLIC_URL}/pdf.worker.min.js?v=${pdfjsLib.version}`;

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
const PADDING = 24;
const PADDING_MOVIL = 8;
// Límite de píxeles por página: los navegadores móviles fallan con canvas muy grandes
const MAX_PIXELES_CANVAS = 8_000_000;

/*
 * El navegador puede mostrar un PDF por sí mismo (escritorio). En Android
 * pdfViewerEnabled es false: ahí el PDF se descarga en lugar de abrirse.
 */
const tieneVisorNativo = () =>
  typeof navigator !== "undefined" && navigator.pdfViewerEnabled === true;

const esPantallaTactil = () =>
  typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;

// Un <iframe> con el PDF solo funciona bien en escritorio (en iOS muestra una sola página)
export const puedeMostrarPdfEnIframe = () => tieneVisorNativo() && !esPantallaTactil();

/**
 * Visor de PDF basado en pdf.js, con resaltado. Funciona igual en escritorio,
 * Android e iOS porque no depende del visor de PDF del navegador.
 *
 * resaltado = { pagina, rects, key }: rects en fracciones del tamaño de
 * la página ([x0, y0, x1, y1]); key cambia en cada clic para volver a
 * desplazar y animar aunque se repita el mismo fragmento.
 */
const VisorPdf = ({ pdfBlob, nombre, resaltado }) => {
  const scrollRef = useRef(null);
  const pageRefs = useRef([]);
  const renderTasks = useRef([]);

  const [pdf, setPdf] = useState(null);
  const [error, setError] = useState(false);
  const [anchoDisponible, setAnchoDisponible] = useState(0);
  const [zoom, setZoom] = useState(1); // 1 = ajustado al ancho
  const [tamanos, setTamanos] = useState([]); // tamaño en pantalla de cada página
  const [paginaActual, setPaginaActual] = useState(1);
  const [pdfUrl, setPdfUrl] = useState(null);

  // En pantallas angostas se reduce el margen para aprovechar el ancho
  const margen = anchoDisponible && anchoDisponible < 600 ? PADDING_MOVIL : PADDING;

  /* =========================
     Cargar documento
  ========================= */
  useEffect(() => {
    if (!pdfBlob) return;
    let cancelado = false;
    let documento = null;
    const url = URL.createObjectURL(pdfBlob);
    setPdfUrl(url);
    setError(false);

    pdfBlob
      .arrayBuffer()
      .then((data) => pdfjsLib.getDocument({ data }).promise)
      .then((doc) => {
        documento = doc;
        if (!cancelado) setPdf(doc);
      })
      .catch((err) => {
        console.error("No se pudo abrir el PDF:", err);
        if (!cancelado) setError(true);
      });

    return () => {
      cancelado = true;
      URL.revokeObjectURL(url);
      documento?.destroy();
    };
  }, [pdfBlob]);

  /* =========================
     Ancho disponible
  ========================= */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      // Oculto (pestaña en móvil) mide 0: se conserva lo ya dibujado
      const ancho = Math.floor(entry.contentRect.width);
      if (ancho > 0) setAnchoDisponible(ancho);
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  /* =========================
     Renderizar páginas
  ========================= */
  useEffect(() => {
    if (!pdf || !anchoDisponible) return;
    let cancelado = false;

    renderTasks.current.forEach((task) => task.cancel());
    renderTasks.current = [];

    const renderizar = async () => {
      // Más de 2x no se nota en pantalla y multiplica la memoria en celulares
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const nuevos = [];

      for (let n = 1; n <= pdf.numPages; n++) {
        const page = await pdf.getPage(n);
        if (cancelado) return;

        const base = page.getViewport({ scale: 1 });
        const escala = ((anchoDisponible - margen * 2) / base.width) * zoom;
        const pixeles = base.width * base.height * (escala * dpr) ** 2;
        const nitidez = Math.min(dpr, dpr * Math.sqrt(MAX_PIXELES_CANVAS / pixeles));
        const viewport = page.getViewport({ scale: escala * nitidez });

        nuevos.push({ ancho: base.width * escala, alto: base.height * escala });
        setTamanos([...nuevos]);

        // Espera a que exista el canvas de esta página
        await new Promise((r) => requestAnimationFrame(r));
        const canvas = pageRefs.current[n - 1]?.querySelector("canvas");
        if (!canvas || cancelado) return;

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        const task = page.render({ canvas, viewport });
        renderTasks.current.push(task);

        try {
          await task.promise;
        } catch (err) {
          if (err?.name !== "RenderingCancelledException") throw err;
          return;
        }
      }
    };

    renderizar().catch((err) => {
      console.error("Error renderizando el PDF:", err);
      if (!cancelado) setError(true);
    });

    return () => {
      cancelado = true;
    };
  }, [pdf, anchoDisponible, zoom, margen]);

  /* =========================
     Ir al fragmento resaltado
  ========================= */
  useEffect(() => {
    if (!resaltado?.pagina || !tamanos[resaltado.pagina - 1]) return;

    const pagina = pageRefs.current[resaltado.pagina - 1];
    const contenedor = scrollRef.current;
    if (!pagina || !contenedor) return;

    const y0 = Math.min(...resaltado.rects.map((r) => r[1]), 1);
    const alto = tamanos[resaltado.pagina - 1].alto;

    contenedor.scrollTo({
      top: Math.max(0, pagina.offsetTop + y0 * alto - contenedor.clientHeight / 3),
      behavior: "smooth",
    });
  }, [resaltado, tamanos]);

  /* =========================
     Página actual al desplazar
  ========================= */
  const handleScroll = useCallback(() => {
    const contenedor = scrollRef.current;
    if (!contenedor) return;

    const referencia = contenedor.scrollTop + contenedor.clientHeight / 3;
    let actual = 1;

    pageRefs.current.forEach((el, i) => {
      if (el && el.offsetTop <= referencia) actual = i + 1;
    });

    setPaginaActual(actual);
  }, []);

  const cambiarZoom = (delta) =>
    setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((z + delta) * 10) / 10)));

  // Sin visor de PDF en el navegador (Android) se ofrece descargar en lugar de abrir
  const visorNativo = tieneVisorNativo();
  const nombreArchivo = /\.pdf$/i.test(nombre || "") ? nombre : `${nombre || "documento"}.pdf`;

  const numPaginas = pdf?.numPages ?? 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #cbd5e1",
        bgcolor: "#e2e8f0",
      }}
    >
      {/* Barra de herramientas */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: { xs: 1, sm: 1.5 },
          py: 0.75,
          bgcolor: "#0F172A",
          color: "#fff",
          minHeight: 48,
        }}
      >
        <DescriptionOutlinedIcon fontSize="small" sx={{ color: "#00C7DB" }} />
        <Tooltip title={nombre || ""}>
          <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: 600, ml: 0.5 }}>
            {nombre || "Documento"}
          </Typography>
        </Tooltip>

        {numPaginas > 0 && (
          <Typography variant="caption" sx={{ px: 1, color: "#cbd5e1", whiteSpace: "nowrap" }}>
            Pág. {paginaActual} / {numPaginas}
          </Typography>
        )}

        <Divider orientation="vertical" flexItem sx={{ borderColor: "#334155", mx: 0.5, display: { xs: "none", sm: "block" } }} />

        <Tooltip title="Alejar">
          <span>
            <IconButton size="small" sx={{ color: "#fff" }} onClick={() => cambiarZoom(-0.1)} disabled={zoom <= ZOOM_MIN}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption" sx={{ width: 40, textAlign: "center", color: "#cbd5e1", display: { xs: "none", sm: "block" } }}>
          {Math.round(zoom * 100)}%
        </Typography>
        <Tooltip title="Acercar">
          <span>
            <IconButton size="small" sx={{ color: "#fff" }} onClick={() => cambiarZoom(0.1)} disabled={zoom >= ZOOM_MAX}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Ajustar al ancho">
          <IconButton size="small" sx={{ color: "#fff" }} onClick={() => setZoom(1)}>
            <FitScreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {visorNativo ? (
          <Tooltip title="Abrir en otra pestaña">
            <span>
              <IconButton
                size="small"
                sx={{ color: "#fff" }}
                disabled={!pdfUrl}
                onClick={() => window.open(pdfUrl, "_blank", "noopener")}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Descargar">
            <span>
              <IconButton
                size="small"
                sx={{ color: "#fff" }}
                disabled={!pdfUrl}
                component="a"
                href={pdfUrl || undefined}
                download={nombreArchivo}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Páginas */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{ flex: 1, minHeight: 0, overflow: "auto", position: "relative" }}
      >
        {error && (
          <Alert severity="warning" sx={{ m: 2 }}>
            No se pudo mostrar el documento.
          </Alert>
        )}

        {!pdf && !error && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {pdf &&
          Array.from({ length: numPaginas }, (_, i) => {
            const tamano = tamanos[i];
            const resaltada = resaltado?.pagina === i + 1;

            return (
              <Box
                key={i}
                ref={(el) => (pageRefs.current[i] = el)}
                sx={{
                  position: "relative",
                  mx: "auto",
                  my: `${margen / 2}px`,
                  width: tamano ? `${tamano.ancho}px` : `calc(100% - ${margen * 2}px)`,
                  height: tamano ? `${tamano.alto}px` : 400,
                  bgcolor: "#fff",
                  boxShadow: "0 2px 10px rgba(15, 23, 42, 0.18)",
                }}
              >
                <canvas style={{ width: "100%", height: "100%", display: "block" }} />

                {resaltada &&
                  resaltado.rects.map(([x0, y0, x1, y1], j) => (
                    <Box
                      key={`${resaltado.key}-${j}`}
                      sx={{
                        position: "absolute",
                        left: `calc(${x0 * 100}% - 2px)`,
                        top: `calc(${y0 * 100}% - 2px)`,
                        width: `calc(${(x1 - x0) * 100}% + 4px)`,
                        height: `calc(${(y1 - y0) * 100}% + 4px)`,
                        bgcolor: "rgba(250, 204, 21, 0.38)",
                        outline: "2px solid #f59e0b",
                        borderRadius: "3px",
                        pointerEvents: "none",
                        mixBlendMode: "multiply",
                        animation: "resaltado-pulso 1.2s ease-out 2",
                        "@keyframes resaltado-pulso": {
                          "0%": { boxShadow: "0 0 0 0 rgba(245, 158, 11, 0.7)" },
                          "100%": { boxShadow: "0 0 0 12px rgba(245, 158, 11, 0)" },
                        },
                      }}
                    />
                  ))}

                <Typography
                  variant="caption"
                  sx={{ position: "absolute", bottom: -2, right: 8, color: "#94a3b8", transform: "translateY(100%)" }}
                >
                  {i + 1}
                </Typography>
              </Box>
            );
          })}
      </Box>
    </Box>
  );
};

export default VisorPdf;
